import { Card, Divider } from "@mui/material";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { AccountStatus, AccountStatusResponse } from "@/interfaces/talk2pay.iterfaces";
import { AccountInfoMessages } from "@/messages/platforms/talk2PayHome/account-Info.messages";
import borders from "@/themes/base/borders";

type Props = {
  messages: AccountInfoMessages;
  status: AccountStatusResponse;
};

export default function AccountStatusCard({ messages, status }: Props) {
  const { borderWidth, borderColor } = borders;

  const getStatusColor = (status: AccountStatus) => {
    switch (status) {
      case AccountStatus.Approved:
        return "success";
      case AccountStatus.Rejected:
        return "error";
      case AccountStatus.Pending:
      case AccountStatus.AwaitingApproval:
        return "warning";
      default:
        return "text";
    }
  };

  const getStatusText = (status: AccountStatus) => {
    switch (status) {
      case AccountStatus.Approved:
        return messages.approvedLabel;
      case AccountStatus.Rejected:
        return messages.rejectedLabel;
      case AccountStatus.Pending:
        return messages.pendingLabel;
      case AccountStatus.AwaitingApproval:
        return messages.awaitingApprovalLabel;
      default:
        return messages.unknownStatusLabel;
    }
  };

  const onRedirect = (href: string) => {
    window.location.href = href;
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h5">{messages.accountStatusTitle}</MDTypography>
        <MDTypography variant="body2" color="text" fontWeight={"regular"}>
          {messages.accountStatusDescription}
        </MDTypography>
      </MDBox>

      <MDBox p={3} pt={0}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          border={`${borderWidth[1]} solid ${borderColor}`}
          borderRadius="md"
          p={2}
        >
          <MDTypography variant="body2" color="text">
            1. {messages.commercialDataCompletion} &nbsp;
          </MDTypography>

          <MDBox
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <MDBox mx={{ xs: 0, sm: 2 }} mb={{ xs: 1, sm: 0 }}>
              <MDTypography
                variant="button"
                color={getStatusColor(status.commercialInfo)}
                fontWeight="regular"
              >
                {getStatusText(status.commercialInfo)}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        <Divider />

        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          border={`${borderWidth[1]} solid ${borderColor}`}
          borderRadius="md"
          p={2}
        >
          <MDTypography variant="body2" color="text">
            2. {messages.documentSubmission} &nbsp;
            {status.documentation === AccountStatus.Rejected && (
              <>
                <MDTypography
                  onClick={() => onRedirect(messages.sendDocumentsHref)}
                  variant="caption"
                  fontWeight="medium"
                  textGradient
                  sx={{ cursor: "pointer", fontSize: "14px" }}
                  color="dark"
                >
                  {messages.sendDocumentsLinkText}
                </MDTypography>
                {messages.sendDocumentsText}
              </>
            )}
          </MDTypography>

          <MDBox
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <MDBox mx={{ xs: 0, sm: 2 }} mb={{ xs: 1, sm: 0 }}>
              <MDTypography
                variant="button"
                color={getStatusColor(status.documentation)}
                fontWeight="regular"
              >
                {getStatusText(status.documentation)}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        <Divider />

        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          border={`${borderWidth[1]} solid ${borderColor}`}
          borderRadius="md"
          p={2}
        >
          <MDTypography variant="body2" color="text">
            3. {messages.generalApproval} &nbsp;
          </MDTypography>

          <MDBox
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <MDBox mx={{ xs: 0, sm: 2 }} mb={{ xs: 1, sm: 0 }}>
              <MDTypography
                variant="button"
                color={getStatusColor(status.general)}
                fontWeight="regular"
              >
                {getStatusText(status.general)}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
        <Divider />
      </MDBox>
    </Card>
  );
}
